// src/hooks/useVariants.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchVariantTypesPaginated,
  fetchVariantTypeStats,
  createVariantType,
  updateVariantType,
  deleteVariantType,
  toggleVariantTypeStatus,
} from '../admin/pages/variant/variantService';
import { VariantTypeFormData } from '../enums/product';

export const VARIANT_KEYS = {
  all:       ['variantTypes'] as const,
  lists:     () => ['variantTypes', 'list'] as const,
  paginated: (page: number, search: string) =>
               ['variantTypes', 'list', page, search] as const,
  stats:     () => ['variantTypes', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface VariantPaginationParams {
  page: number;
  search: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to fetchVariantTypesPaginated instead
// of cursor objects. Any page can now be fetched in any order.
export const useVariantTypesPaginated = (params: VariantPaginationParams) => {
  return useQuery({
    queryKey: VARIANT_KEYS.paginated(params.page, params.search),
    queryFn: () =>
      fetchVariantTypesPaginated(
        params.pageSize,
        params.page,
        params.search || undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useVariantTypeStats = () => {
  return useQuery({
    queryKey: VARIANT_KEYS.stats(),
    queryFn: fetchVariantTypeStats,
    staleTime: 1000 * 60 * 5,
  });
};

export const useVariantMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: VARIANT_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({ formData, userId }: { formData: VariantTypeFormData; userId: string }) =>
      createVariantType(formData, userId),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({ variantTypeId, formData }: { variantTypeId: string; formData: VariantTypeFormData }) =>
      updateVariantType(variantTypeId, formData),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (variantTypeId: string) => deleteVariantType(variantTypeId),
    onSuccess: invalidateAll,
  });

  const toggle = useMutation({
    mutationFn: ({ variantTypeId, isActive }: { variantTypeId: string; isActive: boolean }) =>
      toggleVariantTypeStatus(variantTypeId, isActive),
    onSuccess: invalidateAll,
  });

  return { create, update, remove, toggle };
};